// primitives/input/Input.tsx
import * as React from "react";
import { useControllableState } from "@/ui/form/hooks/useControllableState";

type Base = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange"
>;

type ValueChangeMeta = { name?: string; id?: string };

type Props = Base & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string, meta: ValueChangeMeta) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

export const Input = React.forwardRef<HTMLInputElement, Props>(
  ({ value, defaultValue, onValueChange, onChange, onAnimationStart, ...rest }, ref) => {
    const [val, setVal] = useControllableState<string>({
      value,
      defaultValue,
      onChange: (next) =>
        onValueChange?.(next, { name: rest.name, id: rest.id }),
    });

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;

        // Update internal or controlled state
        setVal(next);

        // Notify library consumer with parsed value and metadata
        onValueChange?.(next, {
          name: e.target.name,
          id: e.target.id,
        });

        // Bubble original DOM event if user supplied handler
        onChange?.(e);
      },
      [onChange, onValueChange, setVal]
    );

   

    return (
      <input
        ref={ref}
        value={val ?? ""}
        onChange={handleChange}
        data-field-input=""
        {...rest}
      />
    );
  }
);

Input.displayName = "Input";
